// ─────────────────────────────────────────────────────────────
// usePartCatalog — single source of truth for the part dropdowns
// (Kategori → Alt Kategori → Parça). Fetches GET /api/parts/catalog/
// ONCE (module-level cache, shared across pages) and falls back to the
// bundled PARTS_TREE if the API is unreachable or not seeded yet.
// Returns { categories: [...], subcategories: {cat: [...]}, parts: {'cat|sub': [...]} }.
// ─────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { getPartCatalog } from '../services/api';
import { PARTS_TREE } from '../models/partModel';

function fromTree(tree) {
  const categories = Object.keys(tree);
  const subcategories = {};
  const parts = {};
  categories.forEach((cat) => {
    const subs = tree[cat]?.subcategories || {};
    subcategories[cat] = Object.keys(subs);
    Object.entries(subs).forEach(([sub, list]) => { parts[`${cat}|${sub}`] = list; });
  });
  return { categories, subcategories, parts };
}

const FALLBACK = fromTree(PARTS_TREE);

let _cache = null;
let _promise = null;

function load() {
  if (_cache) return Promise.resolve(_cache);
  if (!_promise) {
    _promise = getPartCatalog()
      .then((res) => {
        const d = res.data || {};
        _cache = (d.categories && d.categories.length) ? d : FALLBACK;
        return _cache;
      })
      .catch(() => {
        _cache = FALLBACK;     // offline / API error → bundled catalog
        return _cache;
      });
  }
  return _promise;
}

export function usePartCatalog() {
  const [data, setData] = useState(_cache || FALLBACK);

  useEffect(() => {
    let active = true;
    load().then((d) => { if (active) setData(d); });
    return () => { active = false; };
  }, []);

  return data;
}
